import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import StartSvg from '../../utility/Svg/StartSvg';

// ── SVG Icons ──────────────────────────────────────────────────────────────
const TotalReviewsSvg = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <g clip-path="url(#clip0_24001_178239)">
            <path
                d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM6 14V11.53L12.88 4.65C13.08 4.45 13.39 4.45 13.59 4.65L15.36 6.42C15.56 6.62 15.56 6.93 15.36 7.13L8.47 14H6ZM17 14H10.5L12.5 12H17C17.55 12 18 12.45 18 13C18 13.55 17.55 14 17 14Z"
                fill="#006CDD"
            />
        </g>
        <defs>
            <clipPath id="clip0_24001_178239">
                <rect width="24" height="24" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const AvgRatingSvg = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <g clip-path="url(#clip0_24001_178249)">
            <path
                d="M8.57993 10H2.55993C2.06993 10 1.86993 10.62 2.26993 10.91L7.17993 14.42L5.28993 20.52C5.14993 20.98 5.67993 21.36 6.06993 21.07L10.9999 17.31L15.9299 21.06C16.3199 21.35 16.8499 20.98 16.7099 20.51L14.8199 14.41L19.7299 10.9C20.1299 10.62 19.9299 9.99003 19.4399 9.99003H13.4199L11.4699 3.57003C11.3299 3.10003 10.6599 3.10003 10.5099 3.57003L8.57993 10ZM20.8999 20.51L19.4999 15.99L22.4099 13.91C22.8099 13.63 22.6099 13 22.1199 13H20.2399L17.1599 15.2L18.6199 19.92L20.1199 21.06C20.5099 21.35 21.0399 20.97 20.8999 20.51ZM16.9999 8.00003L15.6599 3.58003C15.5199 3.11003 14.8499 3.11003 14.6999 3.58003L14.1299 5.45003L14.9099 8.00003H16.9999Z"
                fill="#006CDD"
            />
        </g>
        <defs>
            <clipPath id="clip0_24001_178249">
                <rect width="24" height="24" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const SentThisWeekSvg = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <g clip-path="url(#clip0_24001_178287)">
            <path
                d="M20 3H19V2C19 1.45 18.55 1 18 1C17.45 1 17 1.45 17 2V3H7V2C7 1.45 6.55 1 6 1C5.45 1 5 1.45 5 2V3H4C2.9 3 2 3.9 2 5V21C2 22.1 2.9 23 4 23H20C21.1 23 22 22.1 22 21V5C22 3.9 21.1 3 20 3ZM19 21H5C4.45 21 4 20.55 4 20V8H20V20C20 20.55 19.55 21 19 21Z"
                fill="#006CDD"
            />
        </g>
        <defs>
            <clipPath id="clip0_24001_178287">
                <rect width="24" height="24" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const GREEN = '#4CAF50';
const RED = '#D32F2F';

function getDelta({ increaseKey, decreaseKey }) {
    const hasIncrease = increaseKey?.value != null;
    const hasDecrease = decreaseKey?.value != null;

    // If neither key is present, treat as neutral (green)
    if (!hasIncrease && !hasDecrease) {
        return { value: 0, direction: 'neutral' };
    }

    if (hasIncrease) return { value: Number(increaseKey.value) || 0, direction: 'up' };
    return { value: Number(decreaseKey.value) || 0, direction: 'down' };
}

function TrendFooter({ direction, value, label }) {
    const isDown = direction === 'down' && value > 0;
    const color = isDown ? RED : GREEN;
    const Icon = isDown ? ArrowDownward : ArrowUpward;
    if (value === 0) {
        return <></>;
    }

    return (
        <Box display="flex" alignItems="center" gap={'4px'} sx={{ color }}>
            <Icon />
            <Typography variant="body2" sx={{ fontWeight: 500, color, fontSize: '18px', letterSpacing: '0.17px' }}>
                {value == '0' ? '' : isDown ? '-' : '+'} {value} {label}
            </Typography>
        </Box>
    );
}

// ── Card config ────────────────────────────────────────────────────────────
// Each card defines what data key to read and how to render its value/footer
const CARD_CONFIG = [
    {
        key: 'totalReviews',
        label: 'TOTAL REVIEWS',
        Icon: TotalReviewsSvg,
        renderValue: (data) => <Typography sx={styles.count}>{data?.total_reviews?.count ?? 0}</Typography>,
        renderFooter: (data) => {
            const delta = getDelta({
                increaseKey: { value: data?.total_reviews?.this_month_increase },
                decreaseKey: { value: data?.total_reviews?.this_month_decrease },
            });
            return <TrendFooter direction={delta.direction} value={delta.value} label="this month" />;
        },
    },
    {
        key: 'avgRating',
        label: 'GOOGLE RATING',
        Icon: AvgRatingSvg,
        renderValue: (data) => (
            <Typography sx={styles.count}>{data?.average_rating?.rating != null ? Number(data.average_rating.rating).toFixed(1) : '0.0'}</Typography>
        ),
        renderFooter: (data) => <StartSvg />,
    },
    {
        key: 'sentThisWeek',
        label: 'SENT THIS WEEK',
        Icon: SentThisWeekSvg,
        renderValue: (data) => <Typography sx={styles.count}>{data?.total_sent?.count ?? 0}</Typography>,
        renderFooter: (data) => {
            const delta = getDelta({
                increaseKey: { value: data?.total_sent?.this_week_increase },
                decreaseKey: { value: data?.total_sent?.this_week_decrease },
            });
            return (
                <TrendFooter
                    direction={delta.direction}
                    value={delta.value}
                    label={`${delta.direction == 'down' ? 'fewer' : 'more'} than last week${data?.total_sent?.last_week_count ? ` (${data.total_sent?.last_week_count})` : ''}`}
                />
            );
        },
    },
];

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
    card: {
        borderRadius: '12px',
        border: '1px solid #E0E0E0',
        background: '#FFF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        padding: '20px 24px',
        flex: '1 0 0',
        alignSelf: 'stretch',
        height: '100%',
    },
    iconLabel: {
        color: '#616161',
        fontFamily: 'Roboto',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '160%',
        letterSpacing: '0.15px',
        textTransform: 'uppercase',
    },
    count: {
        color: 'rgba(0, 0, 0, 0.87)',
        fontFamily: 'Roboto',
        fontSize: '45px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '123.5%',
        letterSpacing: '-1px',
    },
    rating: { color: '#F5A623' },
};

// ─────────────────────────────────────────────────────────────────────────────
const ReviewRequestCards = ({ counts = {} }) => {
    const data = counts?.data ?? counts;
    return (
        <Grid container item spacing={2}>
            {CARD_CONFIG.map(({ key, label, Icon, renderValue, renderFooter }) => (
                <Grid item xs={12} sm={4} key={key}>
                    <Box sx={styles.card}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Icon />
                            <Typography variant="body2" sx={styles.iconLabel}>
                                {label}
                            </Typography>
                        </Box>
                        {renderValue(data)}
                        {renderFooter && renderFooter(data)}
                    </Box>
                </Grid>
            ))}
        </Grid>
    );
};

export default ReviewRequestCards;
